import * as React from 'react'

import {
  ActionButton,
  FieldLabel,
  FieldTitle,
  labelize,
  SectionHelpText,
  sectionStyle,
  SectionTitle,
  SelectInput,
  TextInput,
  Toggle,
} from './puck-config-field-ui'

import {
  type HeaderActionType,
  type HeaderActionVariant,
  type HeaderConfig,
  HEADER_LAYOUT_PRESETS,
  HEADER_SPACING_OPTIONS,
  HEADER_WIDTH_OPTIONS,
} from '@/types/header-footer-config'
import type { MainNavItem } from '@/types/nav.type'

interface HeaderConfigFieldProps {
  value: unknown
  onChange: (value: unknown) => void
  name?: string
}

const ACTION_TYPES: HeaderActionType[] = ['link', 'button', 'icon']
const ACTION_VARIANTS: HeaderActionVariant[] = ['default', 'outline', 'secondary', 'ghost', 'link']

const layoutOptionLabels: Record<HeaderConfig['layoutPreset'] & string, string> = {
  'logo-left': 'Logo left',
  split: 'Split',
  centered: 'Centered',
  'general-v1-overlay': 'General V1 Overlay',
}

function toHeaderConfig(value: unknown): HeaderConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return value as HeaderConfig
}

function asItems(value: MainNavItem[] | undefined): MainNavItem[] {
  return Array.isArray(value) ? value : []
}

type ChildNavItem = NonNullable<MainNavItem['items']>[number]

function asChildItems(value: MainNavItem['items']): ChildNavItem[] {
  return Array.isArray(value) ? value : []
}

function createDefaultChildNavItem(): ChildNavItem {
  return { title: '', href: '', description: '', external: false, items: [] }
}

export function HeaderConfigFieldRender({ value, onChange, name }: HeaderConfigFieldProps) {
  const [draft, setDraft] = React.useState<HeaderConfig>(() => toHeaderConfig(value))

  React.useEffect(() => setDraft(toHeaderConfig(value)), [value])

  const commit = (updater: (prev: HeaderConfig) => HeaderConfig) => {
    setDraft(prev => {
      const next = updater(prev)
      onChange(next)
      return next
    })
  }

  const rightActions = Array.isArray(draft.rightActions) ? draft.rightActions : []
  const navItems = asItems(draft.navItems)

  const updateRightActions = (
    updater: (prev: NonNullable<HeaderConfig['rightActions']>) => NonNullable<HeaderConfig['rightActions']>
  ) => {
    commit(prev => ({ ...prev, rightActions: updater(Array.isArray(prev.rightActions) ? prev.rightActions : []) }))
  }

  const updateNavItems = (updater: (prev: MainNavItem[]) => MainNavItem[]) => {
    commit(prev => ({ ...prev, navItems: updater(asItems(prev.navItems)) }))
  }

  const updateChildNavItems = (parentIndex: number, updater: (prev: ChildNavItem[]) => ChildNavItem[]) => {
    updateNavItems(prev =>
      prev.map((row, rowIndex) =>
        rowIndex === parentIndex
          ? {
              ...row,
              items: updater(asChildItems(row.items)),
            }
          : row
      )
    )
  }

  const updateChildNavItem = (
    parentIndex: number,
    childIndex: number,
    updater: (prev: ChildNavItem) => ChildNavItem
  ) => {
    updateChildNavItems(parentIndex, prev =>
      prev.map((childRow, childRowIndex) => (childRowIndex === childIndex ? updater(childRow) : childRow))
    )
  }

  return (
    <div>
      <FieldTitle title={labelize(name, 'Header Config')} />

      <div style={sectionStyle}>
        <SectionTitle title="Layout" />
        <SectionHelpText text="Control the structure, width, and spacing of the header shell." />
        <div style={{ display: 'grid', gap: '6px', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <label>
            <SelectInput
              value={draft.layoutPreset || 'logo-left'}
              onChange={event =>
                commit(prev => ({ ...prev, layoutPreset: event.target.value as HeaderConfig['layoutPreset'] }))
              }
            >
              {HEADER_LAYOUT_PRESETS.map(option => (
                <option key={option} value={option}>
                  {layoutOptionLabels[option] || option}
                </option>
              ))}
            </SelectInput>
          </label>
          <label>
            <SelectInput
              value={draft.containerWidth || 'full'}
              onChange={event =>
                commit(prev => ({ ...prev, containerWidth: event.target.value as HeaderConfig['containerWidth'] }))
              }
            >
              {HEADER_WIDTH_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SelectInput>
          </label>
          <label>
            <SelectInput
              value={draft.leftSectionWidth || 'auto'}
              onChange={event =>
                commit(prev => ({ ...prev, leftSectionWidth: event.target.value as HeaderConfig['leftSectionWidth'] }))
              }
            >
              {HEADER_WIDTH_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SelectInput>
          </label>
          <label>
            <SelectInput
              value={draft.rightSectionWidth || 'auto'}
              onChange={event =>
                commit(prev => ({
                  ...prev,
                  rightSectionWidth: event.target.value as HeaderConfig['rightSectionWidth'],
                }))
              }
            >
              {HEADER_WIDTH_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SelectInput>
          </label>
          <label>
            <SelectInput
              value={draft.spacing || 'normal'}
              onChange={event => commit(prev => ({ ...prev, spacing: event.target.value as HeaderConfig['spacing'] }))}
            >
              {HEADER_SPACING_OPTIONS.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SelectInput>
          </label>
        </div>
      </div>

      <div style={sectionStyle}>
        <SectionTitle title="Visibility & behavior" />
        <div style={{ display: 'grid', gap: '6px' }}>
          <Toggle
            checked={(draft.sticky ?? true) === true}
            label="Sticky header"
            onChange={next => commit(prev => ({ ...prev, sticky: next }))}
          />
          <Toggle
            checked={draft.showBorder !== false}
            label="Show bottom border"
            onChange={next => commit(prev => ({ ...prev, showBorder: next }))}
          />
          <Toggle
            checked={draft.showLeftSection !== false}
            label="Show left section"
            onChange={next => commit(prev => ({ ...prev, showLeftSection: next }))}
          />
          <Toggle
            checked={draft.showRightSection !== false}
            label="Show right section"
            onChange={next => commit(prev => ({ ...prev, showRightSection: next }))}
          />
          <Toggle
            checked={draft.showForum !== false}
            label="Show forum link"
            onChange={next => commit(prev => ({ ...prev, showForum: next }))}
          />
          <Toggle
            checked={draft.showChat !== false}
            label="Show chat widget"
            onChange={next => commit(prev => ({ ...prev, showChat: next }))}
          />
        </div>
      </div>

      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <SectionTitle title="Right actions" />
          </div>
          <ActionButton
            onClick={() =>
              updateRightActions(prev => [
                ...prev,
                { type: 'button', label: '', href: '', variant: 'default', external: false },
              ])
            }
          >
            Add action
          </ActionButton>
        </div>

        {rightActions.map((action, index) => (
          <div key={`action-${index}`} style={sectionStyle}>
            <div style={{ display: 'grid', gap: '6px', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <label>
                <FieldLabel label="Type" />
                <SelectInput
                  value={action.type || 'button'}
                  onChange={event =>
                    updateRightActions(prev =>
                      prev.map((row, rowIndex) =>
                        rowIndex === index ? { ...row, type: event.target.value as HeaderActionType } : row
                      )
                    )
                  }
                >
                  {ACTION_TYPES.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </SelectInput>
              </label>
              <label>
                <FieldLabel label="Variant" />
                <SelectInput
                  value={action.variant || 'default'}
                  onChange={event =>
                    updateRightActions(prev =>
                      prev.map((row, rowIndex) =>
                        rowIndex === index ? { ...row, variant: event.target.value as HeaderActionVariant } : row
                      )
                    )
                  }
                >
                  {ACTION_VARIANTS.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </SelectInput>
              </label>
            </div>

            <div style={{ display: 'grid', gap: '6px', marginTop: '6px' }}>
              <label>
                <FieldLabel label="Label" helpText="Visible action text." />
                <TextInput
                  placeholder="Join now"
                  value={action.label || ''}
                  onChange={event =>
                    updateRightActions(prev =>
                      prev.map((row, rowIndex) => (rowIndex === index ? { ...row, label: event.target.value } : row))
                    )
                  }
                />
              </label>
              <label>
                <FieldLabel label="Href" helpText="Destination path or URL." />
                <TextInput
                  placeholder="/sign-up"
                  value={action.href || ''}
                  onChange={event =>
                    updateRightActions(prev =>
                      prev.map((row, rowIndex) => (rowIndex === index ? { ...row, href: event.target.value } : row))
                    )
                  }
                />
              </label>
              <label>
                <FieldLabel label="Icon key (optional)" helpText="Used for icon action type." />
                <TextInput
                  placeholder="bookOpen"
                  value={action.icon || ''}
                  onChange={event =>
                    updateRightActions(prev =>
                      prev.map((row, rowIndex) =>
                        rowIndex === index ? { ...row, icon: event.target.value || undefined } : row
                      )
                    )
                  }
                />
              </label>
              <Toggle
                checked={action.external === true}
                label="Open in new tab"
                onChange={next =>
                  updateRightActions(prev =>
                    prev.map((row, rowIndex) => (rowIndex === index ? { ...row, external: next } : row))
                  )
                }
              />
            </div>

            <ActionButton onClick={() => updateRightActions(prev => prev.filter((_, i) => i !== index))}>
              Remove action
            </ActionButton>
          </div>
        ))}
      </div>

      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <SectionTitle title="Desktop navigation" />
          </div>
          <ActionButton onClick={() => updateNavItems(prev => [...prev, { title: '', href: '', items: [] }])}>
            Add nav item
          </ActionButton>
        </div>

        {navItems.map((item, index) => (
          <div key={`nav-${index}`} style={sectionStyle}>
            <label>
              <FieldLabel label="Title" />
              <TextInput
                placeholder="Learning path"
                value={item.title || ''}
                onChange={event =>
                  updateNavItems(prev =>
                    prev.map((row, rowIndex) => (rowIndex === index ? { ...row, title: event.target.value } : row))
                  )
                }
              />
            </label>
            <label>
              <FieldLabel
                label="Href"
                helpText={
                  asChildItems(item.items).length > 0
                    ? 'Disabled while dropdown links exist (runtime ignores parent href).'
                    : 'Leave empty when this item is a dropdown parent.'
                }
              />
              <TextInput
                placeholder="/learning-path"
                value={item.href || ''}
                disabled={asChildItems(item.items).length > 0}
                onChange={event =>
                  updateNavItems(prev =>
                    prev.map((row, rowIndex) => (rowIndex === index ? { ...row, href: event.target.value } : row))
                  )
                }
              />
            </label>

            <div style={{ ...sectionStyle, marginTop: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <SectionTitle title="Dropdown links" />
                <ActionButton
                  onClick={() => {
                    updateChildNavItems(index, prev => [...prev, createDefaultChildNavItem()])
                    updateNavItems(prev =>
                      prev.map((row, rowIndex) => (rowIndex === index ? { ...row, href: '' } : row))
                    )
                  }}
                >
                  Add dropdown link
                </ActionButton>
              </div>

              {asChildItems(item.items).map((childItem, childIndex) => (
                <div key={`nav-${index}-child-${childIndex}`} style={sectionStyle}>
                  <label>
                    <FieldLabel label="Title" />
                    <TextInput
                      placeholder="Machine learning"
                      value={childItem.title || ''}
                      onChange={event =>
                        updateChildNavItem(index, childIndex, prev => ({ ...prev, title: event.target.value }))
                      }
                    />
                  </label>
                  <label>
                    <FieldLabel label="Href" />
                    <TextInput
                      placeholder="/machine-learning"
                      value={childItem.href || ''}
                      onChange={event =>
                        updateChildNavItem(index, childIndex, prev => ({ ...prev, href: event.target.value }))
                      }
                    />
                  </label>
                  <label>
                    <FieldLabel label="Description" />
                    <TextInput
                      placeholder="Browse machine learning tracks"
                      value={childItem.description || ''}
                      onChange={event =>
                        updateChildNavItem(index, childIndex, prev => ({ ...prev, description: event.target.value }))
                      }
                    />
                  </label>
                  <label>
                    <FieldLabel label="Icon key" helpText="Optional icon from Icons map." />
                    <TextInput
                      placeholder="bookOpen"
                      value={childItem.icon || ''}
                      onChange={event =>
                        updateChildNavItem(index, childIndex, prev => ({
                          ...prev,
                          icon: event.target.value ? (event.target.value as ChildNavItem['icon']) : undefined,
                        }))
                      }
                    />
                  </label>
                  <label>
                    <FieldLabel label="Thumbnail" helpText="Optional preview image URL." />
                    <TextInput
                      placeholder="https://example.com/thumbnail.png"
                      value={childItem.thumbnail || ''}
                      onChange={event =>
                        updateChildNavItem(index, childIndex, prev => ({
                          ...prev,
                          thumbnail: event.target.value || undefined,
                        }))
                      }
                    />
                  </label>
                  <label>
                    <FieldLabel label="Dropdown width" helpText="Used when this link also has nested items." />
                    <TextInput
                      type="number"
                      min={0}
                      placeholder="460"
                      value={typeof childItem.width === 'number' ? String(childItem.width) : ''}
                      onChange={event => {
                        const parsedWidth = Number(event.target.value)
                        updateChildNavItem(index, childIndex, prev => ({
                          ...prev,
                          width: Number.isFinite(parsedWidth) && parsedWidth > 0 ? parsedWidth : undefined,
                        }))
                      }}
                    />
                  </label>
                  <Toggle
                    checked={childItem.external === true}
                    label="Open in new tab"
                    onChange={next => updateChildNavItem(index, childIndex, prev => ({ ...prev, external: next }))}
                  />
                  <ActionButton
                    onClick={() => updateChildNavItems(index, prev => prev.filter((_, i) => i !== childIndex))}
                  >
                    Remove dropdown link
                  </ActionButton>
                </div>
              ))}
            </div>

            <ActionButton onClick={() => updateNavItems(prev => prev.filter((_, i) => i !== index))}>
              Remove nav item
            </ActionButton>
          </div>
        ))}

        <div style={{ marginTop: '8px' }}>
          <Toggle
            checked={Array.isArray(draft.mobileNavItems)}
            label="Enable dedicated mobile nav items"
            onChange={next =>
              commit(prev => ({
                ...prev,
                mobileNavItems: next ? (Array.isArray(prev.mobileNavItems) ? prev.mobileNavItems : []) : undefined,
              }))
            }
          />
        </div>
      </div>
    </div>
  )
}
